import React, { useCallback, useEffect, useMemo, useState } from "react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
    Box,
    Chip,
    CircularProgress,
    IconButton,
    Paper,
    Stack,
    Typography,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { ADMIN_SLIDING_PLAY_PATH } from "../adminSlidingRoutes";
import AdminSlidingPlayService from "./AdminSlidingPlayService";

export default function AdminSlidingPlayDetailPage() {
    const { playId } = useParams();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [playRow, setPlayRow] = useState(null);
    const [error, setError] = useState(null);

    const loadDetail = useCallback(async () => {
        if (!playId) return;
        setLoading(true);
        setError(null);
        try {
            const row = await AdminSlidingPlayService.requestPlayDetail(playId);
            setPlayRow(row);
        } catch (e) {
            setError(e?.message || "Failed to load play session");
            setPlayRow(null);
        } finally {
            setLoading(false);
        }
    }, [playId]);

    useEffect(() => {
        loadDetail();
    }, [loadDetail]);

    const layoutPreview = useMemo(() => {
        const layout = playRow?.currentLayout;
        const gs = playRow?.gridSize;
        if (!Array.isArray(layout) || !(gs > 0) || layout.length !== gs * gs) return null;
        return { layout, gs };
    }, [playRow]);

    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ maxWidth: 960 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                <IconButton aria-label="Back to play list" onClick={() => navigate(ADMIN_SLIDING_PLAY_PATH)}>
                    <ArrowBackIcon />
                </IconButton>
                <Typography variant="h6" component="h1">
                    Sliding play session
                </Typography>
            </Stack>

            {error ? (
                <Typography color="error">{error}</Typography>
            ) : playRow ? (
                <Stack spacing={2}>
                    <Paper sx={{ p: 2 }}>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                            <Chip
                                size="small"
                                color={playRow.isCompleted ? "success" : "warning"}
                                label={playRow.status}
                            />
                            {playRow.gridSize ? (
                                <Chip size="small" variant="outlined" label={`${playRow.gridSize}×${playRow.gridSize}`} />
                            ) : null}
                            {playRow.difficulty ? (
                                <Chip size="small" variant="outlined" label={playRow.difficulty} />
                            ) : null}
                            {playRow.tileMode === "numbers" ? (
                                <Chip size="small" variant="outlined" label="Numbers" />
                            ) : null}
                            {playRow.tileMode === "image" ? (
                                <Chip size="small" variant="outlined" label="Image" />
                            ) : null}
                        </Stack>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {playRow.puzzleTitle}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {playRow.name} · {playRow.email}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Started: {playRow.startedAt || "—"} · Finished: {playRow.finishedAt || "—"} · Attempts:{" "}
                            {playRow.attemptsCount ?? 0}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                            Play id: {playRow.id}
                            {playRow.slidingId ? ` · Sliding id: ${playRow.slidingId}` : ""}
                        </Typography>
                    </Paper>

                    {playRow.imageUrl ? (
                        <Paper sx={{ p: 2 }}>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                Story image
                            </Typography>
                            <Box
                                component="img"
                                src={playRow.imageUrl}
                                alt=""
                                sx={{
                                    width: "100%",
                                    maxWidth: 320,
                                    borderRadius: 1,
                                    display: "block",
                                }}
                            />
                        </Paper>
                    ) : null}

                    {layoutPreview ? (
                        <Paper sx={{ p: 2 }}>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                Current layout
                            </Typography>
                            <Box
                                sx={{
                                    display: "grid",
                                    gridTemplateColumns: `repeat(${layoutPreview.gs}, minmax(0, 1fr))`,
                                    gap: 0.5,
                                    maxWidth: 360,
                                }}
                            >
                                {layoutPreview.layout.map((cell, idx) => (
                                    <Box
                                        key={`${idx}-${cell}`}
                                        sx={{
                                            aspectRatio: "1 / 1",
                                            border: "1px solid",
                                            borderColor: "divider",
                                            borderRadius: 0.5,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            fontSize: 11,
                                            bgcolor:
                                                Number(cell) === idx
                                                    ? "rgba(46, 125, 50, 0.12)"
                                                    : "background.default",
                                        }}
                                    >
                                        {cell}
                                    </Box>
                                ))}
                            </Box>
                        </Paper>
                    ) : null}
                </Stack>
            ) : (
                <Typography color="text.secondary">Play session not found.</Typography>
            )}
        </Box>
    );
}
